import {AnyAttrs, AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {addToast} from 'common/components/toast-area/toast-manager';
import {
  attachPaymentMethod,
  fetchClientSecretAndPaymentMethod,
  getCardInfo,
  handleCardSetup,
} from 'media/js/pricing/api/api';
import {PackageId, PricingCurveVariant} from 'media/js/pricing/api/types';
import {ToastType} from 'common/components/toast/types';
import type {PlanBuilder} from 'media/js/pricing/plan-builder-2023/index';
import {PlanBuilderState} from 'media/js/pricing/plan-builder-2023/types';
import {TrackingEvents, TrackingPropertyNames} from 'media/js/pricing/tracking';

import {template} from './template';
import './index.styl';

const INVOICE_PAYMENT_URL_PATH = `/contact-us/contact-us-payment/`;

type PaymentInfoState = {
  reviewBtnLoading: boolean;
  editingCardInfo: boolean;
  focusedPaymentMethod: PaymentMethod;
  selectedPaymentMethod: PaymentMethod;
  allowedPaymentMethod: PaymentMethod;
  billingDataFetched: boolean;
};

enum PaymentMethod {
  NO_METHOD = 0,
  CREDIT_CARD = 1,
  INVOICE = 2,
  BOTH = 3,
}

const PaymentTitleDescription = {
  [PaymentMethod.BOTH]: {
    title: `Enter your payment details`,
    description: `Choose the way you want to pay. Enter your payment details and pay by card now, or request an invoice from our sales team.`,
  },
  [PaymentMethod.INVOICE]: {
    title: `Request an invoice`,
    description: `Submit a request to pay with an invoice. Our sales team will receive the details of your selected plan and assist you in getting started.`,
  },
  [PaymentMethod.CREDIT_CARD]: {
    title: `Enter your payment details`,
    description: `We make payment easy and fast. No need to negotiate with sales teams. Enter your payment details and start building right away.`,
  },
};
export class PaymentInfo extends Component<PaymentInfoState, AnyAttrs, PlanBuilderState, PlanBuilder> {
  static get attrsSchema(): AttrsSchema<AnyAttrs> {
    return {
      ...super.attrsSchema,
      'plan-state': `json`,
      'show-not-charged-today': `boolean`,
      'mp-2023-style': {
        type: `boolean`,
        description: `flag to check whether page should apply new 2023 stying`,
      },
    };
  }

  get config() {
    return {
      template,
      defaultState: {
        selectedPackages: new Set<number>(),
        reviewBtnLoading: false,
        editingCardInfo: false,
        focusedPaymentMethod: PaymentMethod.NO_METHOD,
        selectedPaymentMethod: PaymentMethod.NO_METHOD,
        allowedPaymentMethod: PaymentMethod.NO_METHOD,
        billingDataFetched: false, // checks connectedCallback being called
      },
      helpers: {
        PaymentMethod,
        PaymentTitleDescription,
        createInvoicePaymentLinkUrl: () => {
          const planState = this.app.helpers.getSelectedPlanState();
          const invoiceFormInfo = {
            // eslint-disable-next-line local-rules/naming-conventions
            e: this.app.state.customerInfo?.userInfo?.email,
            q: planState.cap,
            f: planState.billingFrequency,
            p: planState.packageIds.some((id) => id === PackageId.DATA_WAREHOUSE_EXPORT),
            g: planState.packageIds.some((id) => id === PackageId.GROUP_ANALYTICS),
          };
          return (
            INVOICE_PAYMENT_URL_PATH +
            `#` +
            encodeURIComponent(
              Object.entries(invoiceFormInfo)
                .filter(([_, value]) => {
                  // We should exclude package params unless the customer wants the pacakge,
                  // since the form-fill code will add them if the params are present at all.
                  return !!value;
                })
                .map(([key, value]) => {
                  return `${key}=${value}`;
                })
                .join(`&`),
            )
          );
        },
        handleReviewBtnClick: async (ev) => {
          let {cardElement, selectedCountryCode, selectedPostalCode} = ev.detail;
          let accountId,
            clientSecret,
            paymentMethod,
            paymentMethodId = null;
          try {
            this.update({reviewBtnLoading: true});
            accountId = this.app.state.customerInfo.orgInfo.accountId;
            const clientSecretAndPaymentMethod = await fetchClientSecretAndPaymentMethod(accountId);
            clientSecret = clientSecretAndPaymentMethod.clientSecret;
            paymentMethod = clientSecretAndPaymentMethod.paymentMethod;
            paymentMethodId = await handleCardSetup(
              clientSecret,
              paymentMethod && !this.state.editingCardInfo ? paymentMethod : cardElement[`cardNumber`],
            );
          } catch (err) {
            this.update({reviewBtnLoading: false});
            this.app.helpers.trackEventWithPlanProps(TrackingEvents.ErrorDisplayed, {
              [TrackingPropertyNames.ErrorMessage]: err.message,
            });
            addToast({
              type: ToastType.ERROR,
              message: `Card cannot be authorized. Contact your card issuer or try a different card.`,
            });
            return;
          }
          // We now interact with our servers - these errors are our fault
          try {
            const usingExistingCard = this.app.state.billingCard && !this.state.editingCardInfo;
            if (usingExistingCard) {
              selectedCountryCode = this.app.state.billingCard.countryCode;
              selectedPostalCode = this.app.state.billingCard.postalCode;
            } else {
              await attachPaymentMethod(accountId, paymentMethodId, selectedCountryCode, selectedPostalCode);
              const cardInfo = await getCardInfo(accountId);
              this.app.update({billingCard: cardInfo});
            }
            this.app.helpers.handleChangeSelectedPaymentInfo({
              countryCode: selectedCountryCode,
              paymentMethod: paymentMethodId,
              postalCode: selectedPostalCode,
            });
            this.update({reviewBtnLoading: false});
            this.app.helpers.goToNextStep();
          } catch (err) {
            this.update({reviewBtnLoading: false});
            this.app.helpers.trackEventWithPlanProps(TrackingEvents.ErrorDisplayed, {
              [TrackingPropertyNames.ErrorMessage]: err.message,
            });
            addToast({
              type: ToastType.ERROR,
              message: `There was a problem processing your payment information.`,
            });
          }
        },
        toggleEditingCardInfo: () => {
          this.update({editingCardInfo: !this.state.editingCardInfo});
        },
        updateSelectedPaymentMethod: (paymentMethod: PaymentMethod): void => {
          this.update({selectedPaymentMethod: paymentMethod});
        },
        updateSelectedPaymentMethodOnKeystroke: (ev: React.KeyboardEvent, paymentMethod: PaymentMethod): void => {
          if (ev.key === ` ` || ev.key === `Enter`) {
            this.helpers.updateSelectedPaymentMethod(paymentMethod);
          }
        },
        updateFocusedPaymentMethod: (paymentMethod: PaymentMethod): void => {
          this.update({focusedPaymentMethod: paymentMethod});
        },
        updateAllowedPaymentMethod: () => {
          const allowPaymentViaCreditCard =
            !!this.app.state?.allowPaymentViaCreditCard && this.app.state.customerInfo.orgInfo.canPurchaseOnline;
          const allowPaymentViaInvoice =
            this.app.helpers.getSelectedPlanState().curveVariant === PricingCurveVariant.ENTERPRISE ||
            !this.app.state.customerInfo.orgInfo.canPurchaseOnline;
          if (allowPaymentViaCreditCard && allowPaymentViaInvoice) {
            this.update({allowedPaymentMethod: PaymentMethod.BOTH, selectedPaymentMethod: PaymentMethod.CREDIT_CARD});
          } else if (allowPaymentViaCreditCard) {
            this.update({
              allowedPaymentMethod: PaymentMethod.CREDIT_CARD,
              selectedPaymentMethod: PaymentMethod.CREDIT_CARD,
            });
          } else if (allowPaymentViaInvoice) {
            this.update({
              allowedPaymentMethod: PaymentMethod.INVOICE,
              selectedPaymentMethod: PaymentMethod.INVOICE,
            });
          }
        },
      },
    };
  }

  public async connectedCallback() {
    super.connectedCallback();
    if (this.attr(`mp-2023-style`)) {
      this.helpers.updateAllowedPaymentMethod();
    }
    if (!this.app.state.billingCard) {
      const {accountId} = this.app.state.customerInfo.orgInfo;
      const cardInfo = await getCardInfo(accountId);
      this.app.update({billingCard: cardInfo});
    }
    this.app.helpers.handleUpdateBreakdown();
    this.update({billingDataFetched: true});
  }
}

export const PaymentInfoEl = defineAndMakeJsxFactory(`payment-info-2023`, PaymentInfo);
